import React, { useEffect, useState } from 'react';
import { ArrowDownLeft, Calendar, Filter, MoreVertical } from 'lucide-react';

interface CashSummaryReportProps {
  accounts: any[];
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  compareWith: string;
  setCompareWith: (value: string) => void;
  invoices: any[];
  bills: any[];
  setActiveReport: (report: string | null) => void;
}

interface CashFlowLine {
  name: string;
  amount: number;
}

interface CashFlowReport {
  inflows: CashFlowLine[];
  outflows: CashFlowLine[];
  netCashFlow: number;
}

const toAmount = (value: unknown): number => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.abs(amount) : 0;
};

const normalizeCashFlowLines = (lines: unknown): CashFlowLine[] => {
  if (Array.isArray(lines)) {
    return lines.map((line, index) => {
      if (typeof line === 'number') {
        return { name: `Cash flow ${index + 1}`, amount: toAmount(line) };
      }

      const item = line as Record<string, unknown>;
      return {
        name: String(item.name ?? item.accountName ?? item.account ?? item.category ?? `Cash flow ${index + 1}`),
        amount: toAmount(item.amount ?? item.value ?? item.total ?? item.balance)
      };
    });
  }

  if (lines && typeof lines === 'object') {
    return Object.entries(lines as Record<string, unknown>).map(([name, value]) => ({
      name,
      amount: toAmount(typeof value === 'object' && value !== null
        ? (value as Record<string, unknown>).amount ?? (value as Record<string, unknown>).value ?? (value as Record<string, unknown>).total
        : value)
    }));
  }

  return [];
};

const normalizeCashFlowReport = (payload: unknown): CashFlowReport | null => {
  const response = payload as Record<string, unknown>;
  const data = (response?.data ?? response?.report ?? payload) as Record<string, unknown>;
  const inflows = normalizeCashFlowLines(data?.inflows ?? data?.cashInflows);
  const outflows = normalizeCashFlowLines(data?.outflows ?? data?.cashOutflows);
  const netCashFlowValue = data?.netCashFlow ?? data?.net_cash_flow;

  if (!inflows.length && !outflows.length && netCashFlowValue === undefined) {
    return null;
  }

  return {
    inflows,
    outflows,
    netCashFlow: netCashFlowValue === undefined
      ? inflows.reduce((total, line) => total + line.amount, 0) - outflows.reduce((total, line) => total + line.amount, 0)
      : Number(netCashFlowValue) || 0
  };
};

export const CashSummaryReport: React.FC<CashSummaryReportProps> = ({
  accounts,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  compareWith,
  setCompareWith,
  invoices,
  bills,
  setActiveReport
}) => {
  const [cashFlowReport, setCashFlowReport] = useState<CashFlowReport | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('app_auth_token');
    const orgId = localStorage.getItem('selected_org_id');

    if (!token || !orgId) {
      setCashFlowReport(null);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({ startDate, endDate });

    fetch(`/api/organizations/${encodeURIComponent(orgId)}/reports/cash-flow?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal
    })
      .then(async response => {
        if (!response.ok) throw new Error('Unable to load cash flow report');
        return normalizeCashFlowReport(await response.json());
      })
      .then(report => {
        if (!controller.signal.aborted) setCashFlowReport(report);
      })
      .catch(error => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) setCashFlowReport(null);
      });

    return () => controller.abort();
  }, [startDate, endDate]);

  const isWithinRange = (date: string) => {
    return date >= startDate && date <= endDate;
  };

  // Calculate cash movements
  let totalIncome = 0;
  let totalExpenses = 0;
  const incomeAccounts: Record<string, number> = {};
  const expenseAccounts: Record<string, number> = {};
  
  // For cash summary, we only care about paid invoices/bills
  invoices.forEach(inv => {
    if (isWithinRange(inv.date) && inv.status === 'Paid') {
      (inv.items || []).forEach((item: any) => {
        const account = accounts.find(a => a.id === item.accountId || a.code === item.accountId || a.name === item.accountName);
        if (account && account.type === 'Revenue') {
          const amount = item.total || (item.quantity * item.unitPrice);
          incomeAccounts[account.name] = (incomeAccounts[account.name] || 0) + amount;
          totalIncome += amount;
        }
      });
    }
  });

  bills.forEach(bill => {
    if (isWithinRange(bill.date) && bill.status === 'Paid') {
      (bill.items || []).forEach((item: any) => {
        const account = accounts.find(a => a.id === item.accountId || a.code === item.accountId || a.name === item.accountName);
        if (account && account.type === 'Expense') {
          const amount = item.total || (item.quantity * item.unitPrice);
          expenseAccounts[account.name] = (expenseAccounts[account.name] || 0) + amount;
          totalExpenses += amount;
        }
      });
    }
  });

  const fallbackIncomeAccounts = incomeAccounts;
  const fallbackExpenseAccounts = expenseAccounts;
  const fallbackNetCashFlow = totalIncome - totalExpenses;

  const displayedIncomeAccounts = cashFlowReport
    ? Object.fromEntries(cashFlowReport.inflows.map(({ name, amount }) => [name, amount]))
    : fallbackIncomeAccounts;
  const displayedExpenseAccounts = cashFlowReport
    ? Object.fromEntries(cashFlowReport.outflows.map(({ name, amount }) => [name, amount]))
    : fallbackExpenseAccounts;
  const displayedIncome = cashFlowReport
    ? cashFlowReport.inflows.reduce((total, line) => total + line.amount, 0)
    : totalIncome;
  const displayedExpenses = cashFlowReport
    ? cashFlowReport.outflows.reduce((total, line) => total + line.amount, 0)
    : totalExpenses;
  const netCashMovement = cashFlowReport?.netCashFlow ?? fallbackNetCashFlow;
  const openingBalance = 1760.54; // Mock opening balance for demo
  const closingBalance = openingBalance + netCashMovement;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Compare with</span>
            <select 
              value={compareWith}
              onChange={(e) => setCompareWith(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value="None">None</option>
              <option value="Average">Average</option>
              <option value="1 month">1 month</option>
              <option value="2 months">2 months</option>
              <option value="3 months">3 months</option>
              <option value="4 months">4 months</option>
            </select>
          </div>

          <button className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all">
            <Filter className="w-4 h-4" />
            Filter
          </button>

          <button className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all">
            <MoreVertical className="w-4 h-4" />
            More
          </button>

          <button className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all">
            Update
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Cash Summary</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            For the period {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Account</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">{new Date(endDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Average (YTD)</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Variance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              <tr className="bg-[#141414]/5">
                <td colSpan={4} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Income</td>
              </tr>
              {Object.entries(displayedIncomeAccounts).map(([name, amount]) => (
                <tr key={name} className="hover:bg-[#141414]/5 transition-colors">
                  <td className="p-4 text-xs">{name}</td>
                  <td className="p-4 text-right font-mono text-xs">{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right font-mono text-xs">-</td>
                  <td className="p-4 text-right font-mono text-xs">-</td>
                </tr>
              ))}
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Total Income</td>
                <td className="p-4 text-right font-mono text-xs">{displayedIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>

              <tr className="bg-[#141414]/5">
                <td colSpan={4} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Less Expenses</td>
              </tr>
              {Object.entries(displayedExpenseAccounts).map(([name, amount]) => (
                <tr key={name} className="hover:bg-[#141414]/5 transition-colors">
                  <td className="p-4 text-xs">{name}</td>
                  <td className="p-4 text-right font-mono text-xs">{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right font-mono text-xs">-</td>
                  <td className="p-4 text-right font-mono text-xs">-</td>
                </tr>
              ))}
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Total Expenses</td>
                <td className="p-4 text-right font-mono text-xs">{displayedExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>

              <tr className="font-bold bg-[#141414]/5">
                <td className="p-4 text-xs italic serif">Surplus (Deficit)</td>
                <td className="p-4 text-right font-mono text-xs">{netCashMovement.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>

              <tr className="bg-[#141414]/5">
                <td colSpan={4} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Summary</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Opening Balance</td>
                <td className="p-4 text-right font-mono text-xs">{openingBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs">Plus Net Cash Movement</td>
                <td className="p-4 text-right font-mono text-xs">{netCashMovement.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
              <tr className="font-bold border-t-2 border-[#141414]">
                <td className="p-4 text-xs italic serif">Closing Balance</td>
                <td className="p-4 text-right font-mono text-xs">{closingBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
